import React, { createContext, useContext, useState, useEffect } from 'react';
import { COLUMN_GROUPS } from '../utils/colors';

const DataContext = createContext(null);

async function fetchJSON(path) {
  const res = await fetch(process.env.PUBLIC_URL + path);
  if (!res.ok) throw new Error(`Failed to fetch ${path}: ${res.status}`);
  return res.json();
}

export function DataProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleGroups, setVisibleGroups] = useState(() => {
    const initial = {};
    for (const group of Object.keys(COLUMN_GROUPS)) {
      initial[group] = true;
    }
    return initial;
  });

  useEffect(() => {
    // Try new filename first, fall back to old
    fetchJSON('/data/expression.json')
      .catch(() => fetchJSON('/data/expression_9col.json'))
      .then(expression =>
        Promise.all([
          expression,
          fetchJSON('/data/per_sample.json'),
          fetchJSON('/data/de_results.json'),
          fetchJSON('/data/panels.json'),
          fetchJSON('/data/gene_list.json'),
        ])
      )
      .then(([expression, perSample, deResults, panels, geneList]) => {
        const aliasMap = panels.aliases || {};

        // Mutations and transformation data are optional — don't block loading if missing
        const mutationsPromise = fetchJSON('/data/mutations.json').catch(() => null);
        const transformPromise = fetchJSON('/data/transformation.json').catch(() => null);

        return Promise.all([mutationsPromise, transformPromise]).then(([mutations, transformation]) => {
          setData({
            expression,
            perSample,
            deResults,
            panels: panels.panels,
            subcatColors: panels.subcatColors,
            aliases: aliasMap,
            colLabels: panels.colLabels,
            stripGenes: panels.stripGenes,
            geneList,
            mutations,
            transformation,
          });
          setLoading(false);
        });
      })
      .catch(err => {
        console.error('Data loading error:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Resolve gene name (canonical or alias)
  const resolveGene = (query) => {
    if (!data) return null;
    if (data.expression[query]) return query;
    const lower = query.toLowerCase();
    const found = data.geneList.find(g => g.toLowerCase() === lower);
    if (found) return found;
    const canonical = data.aliases[lower];
    if (canonical && data.expression[canonical]) return canonical;
    return null;
  };

  // Filter expression and colLabels by visible column groups
  const getFilteredData = () => {
    if (!data) return { filteredExpression: null, filteredColLabels: [], visibleIndices: [] };

    const visibleIndices = [];
    for (const [group, indices] of Object.entries(COLUMN_GROUPS)) {
      if (visibleGroups[group]) {
        visibleIndices.push(...indices);
      }
    }
    visibleIndices.sort((a, b) => a - b);

    const filteredColLabels = visibleIndices.map(i => data.colLabels[i]);

    // Create a proxy that filters on access (lazy, doesn't duplicate all data)
    const filteredExpression = {};
    for (const [gene, vals] of Object.entries(data.expression)) {
      filteredExpression[gene] = visibleIndices.map(i => vals[i]);
    }

    return { filteredExpression, filteredColLabels, visibleIndices };
  };

  return (
    <DataContext.Provider value={{
      data, loading, error, resolveGene,
      visibleGroups, setVisibleGroups, getFilteredData,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  return useContext(DataContext);
}
